// Dokümanlar — VUK/TTK saklama odaklı: yıl ve türe göre gruplama, çoklu yükleme,
// saklama süresi rozeti, yılın tüm belgelerini toplu indirme.
import { api } from '../api';
import { state, isAdmin, navigate } from '../app';
import { esc, $, $$, setHTML, formatDate, statusLabel, icons } from '../helpers';
import { openSheet } from '../shell';
import { viewFileInApp } from '../pdfview';
import { toast, openModal, closeModal, confirmDialog, setBreadcrumb, skeletonTable } from '../ui';

const TYPES: [string, string][] = [
  ['contract', 'Sözleşme'], ['proposal', 'Teklif'], ['invoice', 'Fatura PDF'],
  ['receipt', 'Dekont'], ['deliverable', 'Teslimat'], ['other', 'Diğer'],
];
// VUK 5 yıl, TTK 10 yıl → uzun olanı esas alıyoruz
const KEEP_YEARS = 10;
let docYear = 'all';
let docType = 'all';
let docQuery = '';

function humanSize(n: number): string {
  if (!n) return '—';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(0) + ' KB';
  return (n / 1024 / 1024).toFixed(1) + ' MB';
}
function keepUntil(created: string): number {
  return new Date(created).getFullYear() + KEEP_YEARS;
}

async function fetchDoc(id: number): Promise<Blob> {
  const res = await fetch('/api/documents/' + id + '/file', { headers: { Authorization: 'Bearer ' + state.token } });
  if (!res.ok) { const d: any = await res.json().catch(() => ({})); throw new Error(d.error || 'Dosya alınamadı'); }
  return res.blob();
}

export async function openDoc(id: number, name: string, download = false) {
  try {
    const blob = await fetchDoc(id);
    if (download) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return;
    }
    // Yeni sekme yerine uygulama içinde: adres çubuğunda blob URL görünmez,
    // iOS Safari'nin PDF'i kaydıramama sorununa da takılmayız.
    const sheet = openSheet(name, '<div id="doc-view"></div>', {
      action: { label: 'İndir', onClick: () => openDoc(id, name, true) },
    });
    const host = sheet.querySelector('#doc-view') as HTMLElement;
    host.innerHTML = '<div class="content-loading"><div class="spinner"></div></div>';
    await viewFileInApp(host, blob, name);
  } catch (err: any) { toast(err.message, 'error'); }
}

export async function renderDocuments() {
  const content = $('#content')!;
  setBreadcrumb([{ label: 'Panel', href: '/dashboard' }, { label: 'Dokümanlar' }]);
  setHTML(content, skeletonTable(5, 4));
  try {
    const data = await api.get('/documents');
    const all: any[] = data.documents || [];
    const s = data.summary || {};
    const years = Array.from(new Set(all.map(d => new Date(d.created_at).getFullYear()))).sort((a, b) => b - a);
    const thisYear = new Date().getFullYear();

    setHTML(content, `<div>
      <div class="page-header"><div><h1>Dokümanlar</h1><p class="page-sub">${s.count || 0} belge · ${humanSize(s.total_size || 0)} · yasal saklama ${KEEP_YEARS} yıl</p></div>
        <div class="page-header-actions">
          <button class="btn btn-secondary" id="bulk-dl"><i data-lucide="folder-down"></i> Yılı indir</button>
          ${isAdmin() ? '<button class="btn btn-primary" id="upload-btn"><i data-lucide="upload"></i> Belge yükle</button>' : ''}
        </div></div>

      ${isAdmin() ? `<div class="dropzone" id="dropzone">
        <i data-lucide="upload-cloud"></i>
        <p><strong>Dosyaları buraya sürükleyin</strong> veya <button type="button" class="link-btn" id="browse-btn">bilgisayarınızdan seçin</button></p>
        <span class="dz-hint">PDF, JPG, PNG · dosya başına en fazla 10 MB · birden fazla seçebilirsiniz</span>
        <input type="file" id="dz-input" multiple accept=".pdf,.png,.jpg,.jpeg" hidden>
      </div>
      <div id="upload-queue"></div>` : `<p class="text-muted" style="font-size:.88rem;margin:-4px 0 14px">Burada size ait belgeler arşivlenir. Bize belge göndermeniz gerekiyorsa <a href="/tickets">Destek</a> bölümünden talebinize ekleyebilirsiniz.</p>`}

      <div class="list-tools">
        <div class="search-input-wrapper"><i data-lucide="search"></i><input type="search" id="doc-search" placeholder="Dosya adı, not, müşteri…" autocomplete="off"></div>
        <select id="doc-year" class="btn btn-secondary" aria-label="Yıl">
          <option value="all">Tüm yıllar</option>
          ${years.map(y => `<option value="${y}">${y}${y === thisYear ? ' (bu yıl)' : ''}</option>`).join('')}
        </select>
      </div>
      <div class="filters-bar" id="doc-types">
        <button class="filter-chip active" data-t="all">Tümü <span class="chip-count">${all.length}</span></button>
        ${TYPES.map(([k, l]) => `<button class="filter-chip" data-t="${k}">${l} <span class="chip-count">${all.filter(d => d.type === k).length}</span></button>`).join('')}
      </div>
      <div id="doc-list"></div>
    </div>`);
    icons();

    const filtered = () => all.filter(d => {
      if (docType !== 'all' && d.type !== docType) return false;
      if (docYear !== 'all' && String(new Date(d.created_at).getFullYear()) !== docYear) return false;
      if (docQuery && ![d.name, d.notes, d.client_name, d.project_title, d.invoice_number].filter(Boolean).join(' ').toLowerCase().includes(docQuery.toLowerCase())) return false;
      return true;
    });

    const draw = () => {
      const rows = filtered();
      const el = $('#doc-list')!;
      if (!rows.length) { setHTML(el, '<div class="empty-state"><i data-lucide="file-archive"></i><p>Bu filtreye uyan belge yok</p></div>'); icons(); return; }
      // Yıla göre grupla
      const groups: Record<string, any[]> = {};
      rows.forEach(d => { const y = String(new Date(d.created_at).getFullYear()); (groups[y] = groups[y] || []).push(d); });
      const html = Object.keys(groups).sort((a, b) => Number(b) - Number(a)).map(y => `
        <div class="doc-group"><div class="doc-group-head"><span>${y}</span><span class="chip chip-muted">${groups[y].length} belge · ${keepUntil(y + '-01-01')} sonuna kadar saklanmalı</span></div>
        <div class="client-list">${groups[y].map(d => `<div class="client-row" style="cursor:default">
          <div class="client-main">
            <div class="client-title">${esc(d.name)}</div>
            <div class="client-sub">${esc(d.notes || '')}</div>
            <div class="client-meta">${formatDate(d.created_at)} · ${humanSize(d.file_size)}${isAdmin() && d.client_name ? ' · ' + esc(d.client_name) : ''}${d.invoice_number ? ' · <span class="font-mono">' + esc(d.invoice_number) + '</span>' : ''}${d.project_title ? ' · ' + esc(d.project_title) : ''}</div>
            <div class="client-chips"><span class="chip chip-muted">${esc(statusLabel(d.type))}</span>${d.has_file ? '' : '<span class="chip chip-warn">Dosya yok</span>'}</div>
          </div>
          <div class="client-fin" style="flex-direction:row;gap:4px;align-items:center">
            ${d.has_file ? `<button class="btn btn-ghost btn-sm" data-view="${d.id}" data-name="${esc(d.name)}" title="Aç"><i data-lucide="eye"></i></button>
            <button class="btn btn-ghost btn-sm" data-dl="${d.id}" data-name="${esc(d.name)}" title="İndir"><i data-lucide="download"></i></button>` : ''}
            ${isAdmin() ? `<button class="btn btn-ghost btn-sm" data-del="${d.id}" title="Sil"><i data-lucide="trash-2"></i></button>` : ''}
          </div></div>`).join('')}</div></div>`).join('');
      setHTML(el, html);
      icons();
      $$('[data-view]').forEach(b => b.addEventListener('click', () => openDoc(parseInt(b.getAttribute('data-view')!), b.getAttribute('data-name')!)));
      $$('[data-dl]').forEach(b => b.addEventListener('click', () => openDoc(parseInt(b.getAttribute('data-dl')!), b.getAttribute('data-name')!, true)));
      $$('[data-del]').forEach(b => b.addEventListener('click', async () => {
        if (await confirmDialog('Belgeyi sil', 'Bu belge kalıcı olarak silinir. Yasal saklama süresi dolmadan silmemeniz önerilir. Devam edilsin mi?', 'Sil', 'danger')) {
          try { await api.del('/documents/' + b.getAttribute('data-del')); toast('Belge silindi', 'success'); renderDocuments(); } catch (err: any) { toast(err.message, 'error'); }
        }
      }));
    };
    draw();

    let t: any;
    (document.getElementById('doc-search') as HTMLInputElement).addEventListener('input', (e) => {
      clearTimeout(t); t = setTimeout(() => { docQuery = (e.target as HTMLInputElement).value.trim(); draw(); }, 150);
    });
    (document.getElementById('doc-year') as unknown as HTMLSelectElement).addEventListener('change', (e) => { docYear = (e.target as HTMLSelectElement).value; draw(); });
    $$('#doc-types .filter-chip').forEach(b => b.addEventListener('click', () => {
      docType = b.getAttribute('data-t')!;
      $$('#doc-types .filter-chip').forEach(x => x.classList.toggle('active', x === b));
      draw();
    }));

    // ── Çoklu yükleme: yalnızca admin. Müşteri belgeyi Destek talebine ekler. ──
    const dz = document.getElementById('dropzone');
    if (dz) {
      const input = document.getElementById('dz-input') as HTMLInputElement;
      const handleFiles = (files: File[]) => { if (files.length) openUploadForm(files); };
      document.getElementById('browse-btn')?.addEventListener('click', () => input.click());
      document.getElementById('upload-btn')?.addEventListener('click', () => input.click());
      dz.addEventListener('click', (e) => { if (e.target === dz || (e.target as Element).tagName === 'P' || (e.target as Element).tagName === 'I') input.click(); });
      ['dragenter', 'dragover'].forEach(ev => dz.addEventListener(ev, (e) => { e.preventDefault(); dz.classList.add('over'); }));
      ['dragleave', 'drop'].forEach(ev => dz.addEventListener(ev, (e) => { e.preventDefault(); dz.classList.remove('over'); }));
      dz.addEventListener('drop', (e: any) => handleFiles(Array.from(e.dataTransfer.files as FileList)));
      input.addEventListener('change', () => { handleFiles(Array.from(input.files || [])); input.value = ''; });
    }

    // ── Yılın belgelerini toplu indir ──
    document.getElementById('bulk-dl')?.addEventListener('click', async () => {
      const rows = filtered().filter(d => d.has_file);
      if (!rows.length) { toast('İndirilecek belge yok', 'info'); return; }
      if (!await confirmDialog('Toplu indirme', `${rows.length} belge tek tek indirilecek. Tarayıcı birden fazla indirmeye izin vermek isteyebilir. Devam?`, 'İndir')) return;
      toast(rows.length + ' belge indiriliyor…', 'info');
      for (const d of rows) { await openDoc(d.id, d.name, true); await new Promise(r => setTimeout(r, 400)); }
    });
  } catch (err: any) { toast(err.message, 'error'); }
}

function openUploadForm(files: File[]) {
  const rows = files.map((f, i) => {
    const tooBig = f.size > 10 * 1024 * 1024;
    const badType = !/\.(pdf|png|jpe?g)$/i.test(f.name);
    return `<div class="up-row ${tooBig || badType ? 'bad' : ''}" data-i="${i}">
      <i data-lucide="${tooBig || badType ? 'alert-circle' : 'file'}"></i>
      <div class="up-info"><span class="up-name">${esc(f.name)}</span><span class="up-meta">${humanSize(f.size)}${tooBig ? ' — 10 MB sınırını aşıyor' : badType ? ' — sadece PDF, JPG, PNG' : ''}</span></div>
      <select class="up-type" data-i="${i}" ${tooBig || badType ? 'disabled' : ''}>${TYPES.map(([k, l]) => `<option value="${k}"${/sozlesme|contract/i.test(f.name) && k === 'contract' ? ' selected' : (/dekont|receipt/i.test(f.name) && k === 'receipt' ? ' selected' : (/fatura|invoice/i.test(f.name) && k === 'invoice' ? ' selected' : ''))}>${l}</option>`).join('')}</select>
      <span class="up-state" data-i="${i}">${tooBig || badType ? 'Atlanacak' : 'Hazır'}</span>
    </div>`;
  }).join('');
  const valid = files.filter(f => f.size <= 10 * 1024 * 1024 && /\.(pdf|png|jpe?g)$/i.test(f.name));

  openModal(files.length > 1 ? files.length + ' belge yükle' : 'Belge yükle',
    `<form id="up-form" class="auth-form" novalidate>
      <div class="up-list">${rows}</div>
      ${isAdmin() ? '<div class="form-group"><label>Müşteri <span class="muted">(isteğe bağlı)</span></label><select name="client_id" id="up-client"><option value="">— Seçilmedi —</option></select></div>' : ''}
      <div class="form-group"><label>Not <span class="muted">(hepsine uygulanır)</span></label><input type="text" name="notes" placeholder="Örn. 2026 hizmet sözleşmesi"></div>
      <div class="modal-footer" style="padding:1rem 0 0"><button type="button" class="btn btn-secondary" id="up-cancel">Vazgeç</button>
      <button type="submit" class="btn btn-primary" ${valid.length ? '' : 'disabled'}>${valid.length} belgeyi yükle</button></div></form>`, 'lg');
  icons();
  if (isAdmin()) {
    api.get('/clients').then(cd => {
      const sel = document.getElementById('up-client') as HTMLSelectElement | null;
      if (sel) sel.innerHTML += (cd.clients || []).filter((c: any) => c.is_active).map((c: any) => `<option value="${c.id}">${esc(c.company || c.name)}</option>`).join('');
    }).catch(() => { /* yoksay */ });
  }
  document.getElementById('up-cancel')?.addEventListener('click', closeModal);
  document.getElementById('up-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    const notes = String(fd.get('notes') || '');
    const clientId = fd.get('client_id') ? parseInt(String(fd.get('client_id'))) : null;
    const btn = (e.target as HTMLFormElement).querySelector('button[type=submit]') as HTMLButtonElement;
    btn.disabled = true;
    let ok = 0, fail = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const stateEl = document.querySelector(`.up-state[data-i="${i}"]`) as HTMLElement;
      if (f.size > 10 * 1024 * 1024 || !/\.(pdf|png|jpe?g)$/i.test(f.name)) { if (stateEl) stateEl.textContent = 'Atlandı'; continue; }
      if (stateEl) stateEl.textContent = 'Yükleniyor…';
      try {
        const b64 = await new Promise<string>((res, rej) => { const r = new FileReader(); r.onload = () => res((r.result as string).split(',')[1]); r.onerror = rej; r.readAsDataURL(f); });
        const type = (document.querySelector(`.up-type[data-i="${i}"]`) as unknown as HTMLSelectElement).value;
        await api.post('/documents', { name: f.name, type, notes, client_id: clientId, mime_type: f.type || 'application/octet-stream', file_key: b64 });
        if (stateEl) { stateEl.textContent = 'Yüklendi'; stateEl.className = 'up-state ok'; }
        ok++;
      } catch (err: any) {
        if (stateEl) { stateEl.textContent = err.message || 'Hata'; stateEl.className = 'up-state err'; }
        fail++;
      }
    }
    toast(ok + ' belge yüklendi' + (fail ? ', ' + fail + ' başarısız' : ''), fail ? 'warning' : 'success');
    if (!fail) { closeModal(); renderDocuments(); } else btn.disabled = false;
  });
}
